import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Inbox, Loader2, Paperclip, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { uploadWorkflowFileAndExtract, WorkflowAttachmentError } from '@/lib/workflowAttachmentUpload';

interface PasteOutputSlotProps {
  toolName?: string;
  isAnalyzing?: boolean;
  /** Required for binary uploads (PDF/DOCX/XLSX); they go through Supabase storage. */
  workflowId?: string;
  onAnalyze: (text: string, meta?: { filename?: string; mimeType?: string }) => void;
}

// MIME types we can read fully in-browser via FileReader.
const TEXT_MIMES = new Set([
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/tab-separated-values',
  'application/json',
  'application/csv',
  'text/x-markdown',
]);

// Extensions we treat as text even when the browser leaves mimeType blank.
const TEXT_EXTS = new Set(['.txt', '.md', '.markdown', '.csv', '.tsv', '.json', '.log', '.xml', '.yaml', '.yml']);

// Extensions that we route through server-side extraction (extract-file-text).
const BINARY_EXTS = new Set(['.pdf', '.docx', '.xlsx']);

function classifyFile(file: File): 'text' | 'binary' | 'unsupported' {
  if (TEXT_MIMES.has(file.type)) return 'text';
  const lower = file.name.toLowerCase();
  for (const ext of TEXT_EXTS) {
    if (lower.endsWith(ext)) return 'text';
  }
  for (const ext of BINARY_EXTS) {
    if (lower.endsWith(ext)) return 'binary';
  }
  if (file.type === 'application/pdf'
    || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return 'binary';
  }
  return 'unsupported';
}

const MAX_TEXT_FILE_BYTES = 5 * 1024 * 1024; // 5MB ceiling for in-browser read

export function PasteOutputSlot({ toolName, isAnalyzing, workflowId, onAnalyze }: PasteOutputSlotProps) {
  const [text, setText] = useState('');
  const [attached, setAttached] = useState<{ name: string; bytes: number; mime: string } | null>(null);
  const [reading, setReading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const disabled = !text.trim() || isAnalyzing || reading;

  const handleSubmit = () => {
    if (disabled) return;
    onAnalyze(
      text.trim(),
      attached ? { filename: attached.name, mimeType: attached.mime } : undefined,
    );
    setText('');
    setAttached(null);
  };

  const handleFile = async (file: File) => {
    const kind = classifyFile(file);

    if (kind === 'unsupported') {
      toast.error(`Cosmo can't read ${file.name} yet. Supported: text (txt/md/csv/json/tsv/yaml) and binary (pdf/docx/xlsx).`);
      return;
    }

    if (kind === 'text') {
      if (file.size > MAX_TEXT_FILE_BYTES) {
        toast.error(`File too large (max ${MAX_TEXT_FILE_BYTES / 1024 / 1024}MB). Paste the text instead, or split the file.`);
        return;
      }
      setReading(true);
      const reader = new FileReader();
      reader.onerror = () => {
        setReading(false);
        toast.error('Could not read that file.');
      };
      reader.onload = () => {
        setReading(false);
        const content = String(reader.result ?? '').trim();
        if (!content) {
          toast.error('That file looks empty.');
          return;
        }
        setText(prev => (prev ? `${prev.trim()}\n\n${content}` : content));
        setAttached({ name: file.name, bytes: file.size, mime: file.type || 'text/plain' });
        toast.success(`Loaded ${file.name}`);
      };
      reader.readAsText(file);
      return;
    }

    // Binary: route through server-side extraction.
    if (!workflowId) {
      toast.error('Binary upload requires an active workflow context.');
      return;
    }
    setReading(true);
    const toastId = toast.loading(`Extracting ${file.name}…`);
    try {
      const result = await uploadWorkflowFileAndExtract(file, workflowId);
      setText(prev => (prev ? `${prev.trim()}\n\n${result.text}` : result.text));
      setAttached({ name: result.filename, bytes: result.bytes, mime: result.mimeType });
      toast.success(`Loaded ${result.filename}`, { id: toastId });
    } catch (err) {
      const message = err instanceof WorkflowAttachmentError
        ? err.message
        : err instanceof Error ? err.message : 'Could not extract that file.';
      toast.error(message, { id: toastId });
    } finally {
      setReading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so picking the same file again still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4 space-y-3">
      <div className="flex items-center gap-2 text-xs">
        <Inbox className="h-3.5 w-3.5 text-primary" />
        <span className="font-medium text-primary">paste output</span>
        <span className="text-muted-foreground">
          once {toolName ?? 'the tool'} finishes, paste the result here or attach a file.
        </span>
      </div>
      <textarea
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`paste ${toolName ?? "the tool"}'s output…`}
        aria-label="Paste tool output"
        disabled={isAnalyzing || reading}
        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-xs font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
      />

      {attached && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs">
          <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="font-medium truncate">{attached.name}</span>
          <span className="text-muted-foreground shrink-0">
            · {(attached.bytes / 1024).toFixed(1)} KB
          </span>
          <button
            type="button"
            onClick={() => setAttached(null)}
            className="ml-auto text-muted-foreground hover:text-foreground"
            aria-label="Remove attachment"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.markdown,.csv,.tsv,.json,.log,.xml,.yaml,.yml,.pdf,.docx,.xlsx,text/plain,text/markdown,text/csv,application/json,text/tab-separated-values,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileInput}
            className="sr-only"
            disabled={isAnalyzing || reading}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isAnalyzing || reading}
            className="h-7 px-2 text-xs gap-1"
          >
            {reading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
            Attach file
          </Button>
          <span className="text-[10px] text-muted-foreground hidden sm:inline">
            text · md · csv · json · pdf · docx · xlsx
          </span>
        </div>
        <Button size="sm" onClick={handleSubmit} disabled={disabled} className="gap-1.5">
          {isAnalyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Analyze
        </Button>
      </div>

      <div className="flex items-start gap-1.5 text-[10px] text-muted-foreground">
        <AlertCircle className="h-2.5 w-2.5 mt-0.5 shrink-0" />
        <span>
          PDFs, Word, and Excel files are extracted on the server. Large files may take a few seconds.
        </span>
      </div>
    </div>
  );
}
